import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  App as AntdApp,
  Breadcrumb,
  Button,
  Card,
  Col,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  ApartmentOutlined,
  ArrowLeftOutlined,
  DeleteOutlined,
  KeyOutlined,
  PlusOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useOne } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { apiClient } from "../../../services/api/apiClient";
import { usePageTitle } from "../../../hooks";
import { PageHeader } from "../../../components/common/PageHeader";
import { IssueTokenModal } from "./IssueTokenModal";

const { Text } = Typography;

interface AuditCompanyDetail {
  id: number;
  name: string;
  slug: string;
  is_active: boolean;
  member_count: number;
  active_token_count: number;
  created: string;
  modified: string;
}

interface Member {
  id: number;
  name: string;
  code: string;
  email: string;
  user_type: number;
  user_type_display: string;
  is_active: boolean;
}

interface TokenRow {
  id: number;
  name: string;
  scopes: string[];
  is_active: boolean;
  last_used_at: string | null;
  notes: string;
  created: string;
  modified: string;
}

interface CandidateAuditor {
  id: number;
  name: string;
  code: string;
  user_type: number;
  audit_company: number | null;
}

export const AuditCompanyAdminShow = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message, modal } = AntdApp.useApp();
  const companyId = Number(id);

  usePageTitle(t("admin_audit_companies.detail_title", "Empresa auditora"));

  const { result: company, query: companyQuery } = useOne<AuditCompanyDetail>({
    resource: "v1/admin/audit-companies",
    id: companyId,
    queryOptions: { enabled: Number.isFinite(companyId) },
  });
  const refetchCompany = companyQuery?.refetch;

  const [members, setMembers] = useState<Member[]>([]);
  const [tokens, setTokens] = useState<TokenRow[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingTokens, setLoadingTokens] = useState(false);

  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [candidates, setCandidates] = useState<CandidateAuditor[]>([]);
  const [candidateId, setCandidateId] = useState<number | undefined>(undefined);
  const [force, setForce] = useState(false);
  const [addSaving, setAddSaving] = useState(false);

  const [issueOpen, setIssueOpen] = useState(false);

  const loadMembers = async () => {
    if (!companyId) return;
    setLoadingMembers(true);
    try {
      const resp = await apiClient.get<Member[]>(
        `/v1/admin/audit-companies/${companyId}/members/`,
      );
      setMembers(resp.data ?? []);
    } catch {
      message.error(
        t("admin_audit_companies.load_members_failed", "Error cargando miembros"),
      );
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadTokens = async () => {
    if (!companyId) return;
    setLoadingTokens(true);
    try {
      const resp = await apiClient.get<TokenRow[]>(
        `/v1/admin/audit-companies/${companyId}/tokens/`,
      );
      setTokens(resp.data ?? []);
    } catch {
      message.error(
        t("admin_audit_companies.load_tokens_failed", "Error cargando tokens"),
      );
    } finally {
      setLoadingTokens(false);
    }
  };

  // Carga la lista de auditores (type 4/5) elegibles para asignar. Se
  // consulta a demanda al abrir el modal — mantener en memoria la
  // lista completa de auditores sería malgasto si el admin no va a
  // añadir miembros en esta sesión.
  const loadCandidates = async () => {
    try {
      const resp = await apiClient.get<{ results: CandidateAuditor[] }>(
        `/v1/admin/auditor-assignments/?page_size=500`,
      );
      const rows = resp.data?.results ?? [];
      setCandidates(rows);
    } catch {
      message.error(
        t("admin_audit_companies.load_candidates_failed", "Error cargando auditores"),
      );
    }
  };

  useEffect(() => {
    void loadMembers();
    void loadTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const openAddMember = async () => {
    await loadCandidates();
    setCandidateId(undefined);
    setForce(false);
    setAddMemberOpen(true);
  };

  const submitAddMember = async () => {
    if (!candidateId) return;
    setAddSaving(true);
    try {
      await apiClient.post(
        `/v1/admin/audit-companies/${companyId}/members/`,
        { app_user_id: candidateId, force },
      );
      message.success(
        t("admin_audit_companies.member_added", "Auditor añadido"),
      );
      setAddMemberOpen(false);
      void loadMembers();
      void refetchCompany();
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { detail?: string } } }).response;
      if (resp?.status === 409) {
        // Pedir confirmación al admin para reasignar entre empresas.
        modal.confirm({
          title: t(
            "admin_audit_companies.reassign_title",
            "Auditor ya asignado a otra empresa",
          ),
          content: t(
            "admin_audit_companies.reassign_body",
            "Este auditor pertenece a otra empresa. ¿Confirmas reasignarlo? El histórico de auditorías no se mueve.",
          ),
          okText: t("admin_audit_companies.reassign_confirm", "Reasignar"),
          okButtonProps: { danger: true },
          cancelText: t("common.cancel", "Cancelar"),
          onOk: async () => {
            try {
              await apiClient.post(
                `/v1/admin/audit-companies/${companyId}/members/`,
                { app_user_id: candidateId, force: true },
              );
              message.success(
                t("admin_audit_companies.member_reassigned", "Auditor reasignado"),
              );
              setAddMemberOpen(false);
              void loadMembers();
              void refetchCompany();
            } catch {
              message.error(
                t("admin_audit_companies.member_reassign_failed", "No se pudo reasignar"),
              );
            }
          },
        });
      } else {
        message.error(
          resp?.data?.detail ||
            t("admin_audit_companies.member_add_failed", "No se pudo añadir"),
        );
      }
    } finally {
      setAddSaving(false);
    }
  };

  const removeMember = async (member: Member) => {
    try {
      await apiClient.delete(
        `/v1/admin/audit-companies/${companyId}/members/${member.id}/`,
      );
      message.success(
        t("admin_audit_companies.member_removed", "Auditor desvinculado"),
      );
      void loadMembers();
      void refetchCompany();
    } catch {
      message.error(
        t("admin_audit_companies.member_remove_failed", "No se pudo quitar"),
      );
    }
  };

  const toggleToken = async (token: TokenRow, nextActive: boolean) => {
    try {
      await apiClient.patch(
        `/v1/admin/audit-companies/${companyId}/tokens/${token.id}/`,
        { is_active: nextActive },
      );
      message.success(
        nextActive
          ? t("admin_audit_companies.token_reactivated", "Token reactivado")
          : t("admin_audit_companies.token_revoked", "Token revocado"),
      );
      void loadTokens();
      void refetchCompany();
    } catch {
      message.error(
        t("admin_audit_companies.token_toggle_failed", "No se pudo actualizar el token"),
      );
    }
  };

  const breadcrumb = (
    <Breadcrumb
      items={[
        { title: t("home.title", "Inicio") },
        {
          title: (
            <a onClick={() => navigate("/admin/audit-companies")}>
              {t("admin_audit_companies.title", "Empresas auditoras")}
            </a>
          ),
        },
        { title: company?.name ?? id },
      ]}
    />
  );

  return (
    <Card>
      <PageHeader
        icon={<ApartmentOutlined />}
        accent="#13C2C2"
        title={company?.name ?? t("common.loading", "Cargando…")}
        subtitle={
          company
            ? `${company.slug} · ${
                company.is_active
                  ? t("admin_audit_companies.active", "Activa")
                  : t("admin_audit_companies.inactive", "Inactiva")
              }`
            : undefined
        }
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/admin/audit-companies")}
          >
            {t("common.back", "Volver")}
          </Button>
        }
      />
      {breadcrumb}

      <Row gutter={16} style={{ marginTop: 16 }}>
        {/* ----------------------------- Miembros ----------------------------- */}
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={
              <Space>
                <UserAddOutlined />
                {t("admin_audit_companies.members_panel_title", "Miembros")}
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                onClick={openAddMember}
              >
                {t("admin_audit_companies.add_member", "Añadir")}
              </Button>
            }
          >
            <Table<Member>
              dataSource={members}
              loading={loadingMembers}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: t("admin_audit_companies.member_name", "Nombre"),
                  dataIndex: "name",
                  key: "name",
                },
                {
                  title: t("admin_audit_companies.member_code", "Código"),
                  dataIndex: "code",
                  key: "code",
                  width: 110,
                },
                {
                  title: t("admin_audit_companies.member_type", "Tipo"),
                  dataIndex: "user_type_display",
                  key: "user_type",
                  width: 160,
                },
                {
                  title: t("admin_audit_companies.member_active", "Activo"),
                  dataIndex: "is_active",
                  key: "is_active",
                  width: 90,
                  align: "center" as const,
                  render: (v: boolean) =>
                    v ? (
                      <Tag color="green">{t("common.yes", "Sí")}</Tag>
                    ) : (
                      <Tag>{t("common.no", "No")}</Tag>
                    ),
                },
                {
                  title: "",
                  key: "actions",
                  width: 50,
                  render: (_: unknown, record: Member) => (
                    <Popconfirm
                      title={t(
                        "admin_audit_companies.member_remove_confirm",
                        "¿Quitar este auditor de la empresa? Sus auditorías históricas no se mueven.",
                      )}
                      onConfirm={() => removeMember(record)}
                      okText={t("common.remove", "Quitar")}
                      okButtonProps={{ danger: true }}
                      cancelText={t("common.cancel", "Cancelar")}
                    >
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  ),
                },
              ]}
            />
          </Card>
        </Col>

        {/* ----------------------------- Tokens ----------------------------- */}
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={
              <Space>
                <KeyOutlined />
                {t("admin_audit_companies.tokens_panel_title", "Tokens API")}
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                onClick={() => setIssueOpen(true)}
              >
                {t("admin_audit_companies.issue_token", "Emitir token")}
              </Button>
            }
          >
            <Table<TokenRow>
              dataSource={tokens}
              loading={loadingTokens}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: t("admin_audit_companies.token_name_col", "Nombre"),
                  dataIndex: "name",
                  key: "name",
                },
                {
                  title: t("admin_audit_companies.token_scopes_col", "Scopes"),
                  dataIndex: "scopes",
                  key: "scopes",
                  render: (sc: string[]) => (
                    <>
                      {sc.map((s) => (
                        <Tag key={s} style={{ marginBottom: 2 }}>
                          {s}
                        </Tag>
                      ))}
                    </>
                  ),
                },
                {
                  title: t("admin_audit_companies.token_last_used", "Último uso"),
                  dataIndex: "last_used_at",
                  key: "last_used_at",
                  width: 140,
                  render: (v: string | null) =>
                    v ? dayjs(v).format("YYYY-MM-DD HH:mm") : (
                      <Text type="secondary">—</Text>
                    ),
                },
                {
                  title: t("admin_audit_companies.token_active_col", "Activo"),
                  dataIndex: "is_active",
                  key: "is_active",
                  width: 90,
                  align: "center" as const,
                  render: (v: boolean, record: TokenRow) => (
                    <Switch
                      checked={v}
                      size="small"
                      onChange={(next) => toggleToken(record, next)}
                    />
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Add member modal (selector de auditor) */}
      <Modal
        open={addMemberOpen}
        onCancel={() => setAddMemberOpen(false)}
        onOk={submitAddMember}
        okButtonProps={{ loading: addSaving, disabled: !candidateId }}
        okText={t("admin_audit_companies.add_member", "Añadir")}
        cancelText={t("common.cancel", "Cancelar")}
        title={t("admin_audit_companies.add_member_title", "Añadir auditor a la empresa")}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Select<number>
            showSearch
            allowClear
            value={candidateId}
            onChange={(v) => setCandidateId(v)}
            placeholder={t(
              "admin_audit_companies.add_member_select_placeholder",
              "Busca un auditor por nombre o código",
            )}
            style={{ width: "100%" }}
            optionFilterProp="label"
            options={candidates
              .filter((c) => c.audit_company !== companyId)
              .map((c) => ({
                value: c.id,
                label: `${c.name} · ${c.code} · ${
                  c.user_type === 4
                    ? t("admin_audit_companies.user_type_manager", "Jefe")
                    : t("admin_audit_companies.user_type_auditor", "Auditor")
                }${
                  c.audit_company
                    ? ` · ${t("admin_audit_companies.already_in_other", "en otra empresa")}`
                    : ""
                }`,
              }))}
          />
          <Text type="secondary">
            {t(
              "admin_audit_companies.add_member_hint",
              "Si el auditor ya pertenece a otra empresa se te pedirá confirmación antes de reasignarlo.",
            )}
          </Text>
        </Space>
      </Modal>

      {/* Issue token modal */}
      <IssueTokenModal
        open={issueOpen}
        companyId={companyId}
        onClose={() => setIssueOpen(false)}
        onIssued={() => {
          void loadTokens();
          void refetchCompany();
        }}
      />
    </Card>
  );
};

export default AuditCompanyAdminShow;
